import { useEffect, useRef, useCallback } from 'react';
import { getGatewayUrl } from '@halo/web-lib';

type IngestionEventType =
  | 'day:status'
  | 'run:status'
  | 'config:status'
  | 'queue:update'
  | 'backfill:progress';

interface UseIngestionSSEOptions {
  storeId?: number;
  platform?: string;
  /** Called on any matching event. Use to trigger refetches. */
  onEvent: (type: IngestionEventType, payload: Record<string, unknown>) => void;
  /** Disable the SSE connection (e.g. when component is not visible) */
  enabled?: boolean;
}

/**
 * Hook that subscribes to the ingestion admin SSE stream.
 * Automatically reconnects on disconnect.
 */
export function useIngestionSSE({ storeId, platform, onEvent, enabled = true }: UseIngestionSSEOptions) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;

    const baseUrl = getGatewayUrl();
    const params = new URLSearchParams();
    if (storeId) params.set('storeId', String(storeId));
    if (platform) params.set('platform', platform);
    const url = `${baseUrl}/api/v1/ingestion/admin/events?${params}`;

    let eventSource: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      eventSource = new EventSource(url);

      const eventTypes: IngestionEventType[] = [
        'day:status',
        'run:status',
        'config:status',
        'queue:update',
        'backfill:progress',
      ];

      for (const type of eventTypes) {
        eventSource.addEventListener(type, (e: MessageEvent) => {
          try {
            const payload = JSON.parse(e.data);
            onEventRef.current(type, payload);
          } catch {
            // Ignore parse errors
          }
        });
      }

      eventSource.onerror = () => {
        eventSource?.close();
        // Reconnect after 5s
        reconnectTimer = setTimeout(connect, 5_000);
      };
    }

    connect();

    return () => {
      eventSource?.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [storeId, platform, enabled]);
}
